export type RoadClass =
  | 'motorway'
  | 'trunk'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'residential'
  | 'service'
  | 'unclassified'
  | 'track'
  | 'motorway_link'
  | 'trunk_link'
  | 'primary_link'
  | 'secondary_link'
  | 'tertiary_link'
  | 'living_street'
  | 'road'
  | 'footway'
  | 'path'
  | 'cycleway'
  | 'pedestrian'
  | 'platform'
  | 'bridleway'
  | 'steps'
  | 'corridor';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface GeocodeResult {
  lat: number;
  lng: number;
  displayName: string;
}

export interface OsmNode {
  id: number;
  lat: number;
  lng: number;
}

export interface OsmWay {
  id: number;
  nodes: number[];
  roadClass: RoadClass;
  name?: string;
  oneway: boolean;
  lanes?: number;
  /** OSM bridge=yes — deck is elevated, doesn't follow terrain. */
  bridge?: boolean;
  /** OSM tunnel=yes — road runs underground; skip surface rendering. */
  tunnel?: boolean;
  /** OSM layer=N — stacking order tiebreaker when bridges/roads overlap. */
  layer?: number;
}

export interface OsmBuilding {
  /** Way or relation id. */
  id: number;
  /** Outer ring of node ids (closed: first === last). */
  outer: number[];
  /** Optional inner rings (holes). */
  inners?: number[][];
  /** Parsed height in meters, or undefined if unknown. */
  heightMeters?: number;
  /** Building type tag (`yes`, `house`, `commercial`, …). */
  kind?: string;
  /** OSM amenity tag, if present on the building itself or a node inside its footprint
   *  (restaurant, fast_food, cafe, bar, pub, ice_cream, …). Used by delivery mode to seed
   *  pickup candidates. */
  amenity?: string;
  /** OSM name tag, if present. Used as the display name for pickup/dropoff in delivery mode. */
  name?: string;
}

export interface OsmLanduseArea {
  id: number;
  /** Outer ring of node ids. */
  outer: number[];
  inners?: number[][];
  /** e.g. 'forest', 'wood', 'park'. */
  kind: string;
  /** Normalized terrain cover classification — computed server-side from OSM tags. */
  cover: TerrainCover;
}

export type TerrainCover =
  | 'grass'
  | 'scrub'
  | 'farmland'
  | 'sand'
  | 'water'
  | 'rock'
  | 'forest';

export type OsmWaterKind = 'natural-water' | 'river-poly' | 'river-buffered';

export interface OsmWaterArea {
  id: number;
  /** Outer ring of node ids (closed: first === last). */
  outer: number[];
  /** Optional inner rings (islands). */
  inners?: number[][];
  kind: OsmWaterKind;
}

export interface OsmTreeNode {
  id: number;
  lat: number;
  lng: number;
}

export interface OsmCrossing {
  id: number;
  lat: number;
  lng: number;
  /** crossing= sub-tag if present: zebra, marked, uncontrolled, traffic_signals, ... */
  kind?: string;
}

/** OSM `admin_level` values we surface to the client.
 *  6 = county-ish; 7 = Swedish kommun / US township (treated as city tier);
 *  8 = city / town / municipality; 9 = suburb / district;
 *  10 = neighbourhood / quarter. */
export type OsmAdminLevel = 6 | 7 | 8 | 9 | 10;

/** A region polygon (administrative boundary) parsed from an OSM relation. Used by the
 *  client to detect when the player crosses into a new named region. Stored on the
 *  chunk that triggered the fetch, but deduplicated client-side by `id`. */
export interface OsmAdminBoundary {
  /** OSM relation id — stable across chunks; clients dedupe on this. */
  id: number;
  name: string;
  adminLevel: OsmAdminLevel;
  /** Closed outer rings in [lng, lat] order. A multipolygon may have more than one ring. */
  rings: Array<Array<[number, number]>>;
}

/** OSM `place=*` node — a named settlement / district / neighbourhood point feature.
 *  Many cities (e.g. Uppsala) map their neighbourhoods as suburb-nodes rather than as
 *  admin-boundary polygons, so the client supplements polygon point-in-polygon with
 *  nearest-place-within-radius detection. */
export type OsmPlaceKind =
  | 'city'
  | 'town'
  | 'village'
  | 'suburb'
  | 'neighbourhood'
  | 'quarter'
  | 'hamlet'
  | 'locality';

export interface OsmPlace {
  /** OSM node id — stable across chunks; clients dedupe on this. */
  id: number;
  name: string;
  kind: OsmPlaceKind;
  lat: number;
  lng: number;
}

export interface OsmData {
  nodes: Record<number, OsmNode>;
  ways: OsmWay[];
  buildings: OsmBuilding[];
  /** Forest/wood/scrub areas — kept as a separate field for tree scatter. */
  forests: OsmLanduseArea[];
  /** All classified landuse / natural / leisure polygons (includes forests too). */
  landuseAreas: OsmLanduseArea[];
  /** Water bodies — multipolygon lakes + buffered rivers. Rendered with the PBR water material. */
  water: OsmWaterArea[];
  trees: OsmTreeNode[];
  crossings: OsmCrossing[];
  /** Administrative-boundary polygons whose bbox intersects this chunk. The polygons
   *  themselves may span far beyond the chunk — the client deduplicates by id. */
  boundaries: OsmAdminBoundary[];
  /** Named place nodes (city / suburb / neighbourhood / hamlet point features) inside
   *  this chunk. Many cities map their neighbourhoods as points instead of polygons;
   *  the client uses nearest-neighbour over the union of all loaded chunks' places. */
  places: OsmPlace[];
}

export interface ElevationGrid {
  width: number;
  height: number;
  /** Bounding box of the grid in lat/lng */
  south: number;
  west: number;
  north: number;
  east: number;
  /** Elevation in meters, row-major, height x width. Base64-encoded Float32Array on the wire. */
  values: string;
}

export interface ChunkPayload {
  origin: LatLng;
  sizeMeters: number;
  osm: OsmData;
  elevation: ElevationGrid;
}

export const DEFAULT_LOCATION: GeocodeResult = {
  lat: 59.3293,
  lng: 18.0686,
  displayName: 'Basarbovägen 1',
};

// ---------------------------------------------------------------------------
// Multiplayer net protocol
// ---------------------------------------------------------------------------

export type PlayerId = string;

/** sRGB color, components in 0..1. */
export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

export interface PlayerIdentity {
  id: PlayerId;
  name: string;
  color: RgbColor;
}

/**
 * Snapshot of a player's vehicle state.
 *
 * Position is expressed in **geographic coordinates** (lat/lng/alt) so each
 * client can translate into its own local world frame — different players
 * have different SessionFrame origins.
 */
export interface PlayerSnapshot {
  id: PlayerId;
  /** Server time at send, ms since unix epoch. */
  t: number;
  lat: number;
  lng: number;
  /** Altitude in meters (world Y above origin sea-level baseline). */
  alt: number;
  /** Rotation quaternion (world-aligned; tangent-plane skew is acceptable for prototype). */
  qx: number;
  qy: number;
  qz: number;
  qw: number;
  /** Linear velocity in m/s, world axes (for short-window extrapolation). */
  vx: number;
  vy: number;
  vz: number;
  /** Current steering angle, -1..1. */
  steer: number;
  /** Forward speed in m/s (for wheel spin animation). */
  speed: number;
}

export type ClientMsg =
  | { kind: 'hello'; name: string }
  | { kind: 'snapshot'; snap: Omit<PlayerSnapshot, 'id'> }
  | {
      kind: 'tree_fell';
      chunkKey: string;
      treeIndex: number;
      hitDirX: number;
      hitDirZ: number;
    }
  | DeliveryClientMsg;

export type ServerMsg =
  | {
      kind: 'welcome';
      you: PlayerIdentity;
      sessionId: string;
      /** Wall-clock ms at server boot — anchor for session-relative time. */
      sessionEpoch: number;
      /** Wall-clock ms now on the server — used by the client to compute clockOffset. */
      serverNow: number;
      players: Array<PlayerIdentity & { lastSnapshot?: PlayerSnapshot }>;
    }
  | { kind: 'rejected'; reason: 'full' | 'bad_name' }
  | { kind: 'player_joined'; player: PlayerIdentity }
  | { kind: 'player_left'; id: PlayerId }
  | { kind: 'snapshot'; snap: PlayerSnapshot }
  | {
      kind: 'tree_fell';
      chunkKey: string;
      treeIndex: number;
      hitDirX: number;
      hitDirZ: number;
    }
  | DeliveryServerMsg;

export const MAX_PLAYERS = 4;

// ---------------------------------------------------------------------------
// Delivery mode
// ---------------------------------------------------------------------------

export type FoodKind =
  | 'pizza'
  | 'burger'
  | 'sushi'
  | 'coffee'
  | 'taco'
  | 'noodles'
  | 'icecream';

export type SessionMode = 'freeroam' | 'coop' | 'competitive';

export type OrderStatus = 'pinged' | 'picked_up' | 'delivered' | 'expired';

export interface OrderPoint {
  /** Stable lat/lng — survives chunk unload. */
  lat: number;
  lng: number;
  /** OSM building id we anchored to, or undefined for fallback random / amenity-node-only. */
  buildingId?: number;
  /** Display name (e.g. real OSM `name=*` or generated like "Pizza Palace"). */
  name: string;
}

/** One record of a player having delivered this order. */
export interface OrderCompletion {
  playerId: PlayerId;
  /** Server time (ms) the dropoff happened. */
  t: number;
}

/**
 * A simple shared "pickup → dropoff" task. Every player on-clock can pick it up once
 * (creates their own {@link ActiveDelivery}). Race-position completions reduce later
 * arrivers' payout. Co-op also layers a restaurant-streak multiplier on top.
 */
export interface Order {
  id: string;
  food: FoodKind;
  pickup: OrderPoint;
  dropoff: OrderPoint;
  /** Server time (ms) the order was pinged. */
  pingedAt: number;
  /** Server time (ms) at which pickup-speed bonus reaches zero (linear from pingedAt). */
  pickupBonusUntil: number;
  /** Server time (ms) hard expiry — order is reaped after this. */
  expiresAt: number;
  /** Race-position log used for the diminishing bonus tier. */
  completions: OrderCompletion[];
  /** Players who currently have an in-flight ActiveDelivery for this order (drives the
   *  client's "claimed by other" dimming hint). */
  pickedUpBy: PlayerId[];
  status: OrderStatus;
  /** Base pay (before bonuses) for this delivery. */
  basePay: number;
  /** Premium long-distance order (3x pay) — UI tints the pin/beam. */
  premium: boolean;
}

/**
 * One in-flight delivery the player is currently carrying. Each pickup creates its own
 * ActiveDelivery with its own timer; the player can have many at once. Replaces the
 * inventory-slot system — the HUD renders one "timer pill" per active delivery.
 */
export interface ActiveDelivery {
  /** Stable id for client↔server matching. */
  id: string;
  orderId: string;
  food: FoodKind;
  pickupName: string;
  dropoffName: string;
  /** Dropoff lat/lng — lets the client point an arrow at it without re-resolving. */
  dropoffLat: number;
  dropoffLng: number;
  /** Server time (ms) this player picked the order up. */
  pickedUpAt: number;
  /** Server time (ms) at which this delivery's speed-bonus reaches zero. */
  bonusUntil: number;
  /** Total length of the delivery window in ms — used by the client to draw a 0..1 bar. */
  windowMs: number;
  basePay: number;
  premium: boolean;
}

export interface DeliveryScore {
  /** Score for the current shift; resets on mode change / off-clock. */
  sessionScore: number;
  /** Persistent money accumulated across sessions. */
  persistentMoney: number;
}

export interface DeliveryStateSelf {
  active: ActiveDelivery[];
  score: DeliveryScore;
  onClock: boolean;
  isHost: boolean;
}

export type DeliveryClientMsg =
  | { kind: 'delivery_clock'; on: boolean }
  | { kind: 'delivery_set_mode'; mode: SessionMode };

export type DeliveryServerMsg =
  | {
      kind: 'delivery_state';
      mode: SessionMode;
      orders: Order[];
      you: DeliveryStateSelf;
    }
  | { kind: 'delivery_order_spawned'; order: Order }
  | { kind: 'delivery_order_updated'; order: Order }
  | { kind: 'delivery_order_removed'; orderId: string }
  | { kind: 'delivery_active'; active: ActiveDelivery[] }
  | { kind: 'delivery_score'; score: DeliveryScore }
  | { kind: 'delivery_mode_changed'; mode: SessionMode; setBy: PlayerId }
  | { kind: 'delivery_host_changed'; hostId: PlayerId }
  | { kind: 'delivery_on_clock'; on: boolean }
  | {
      kind: 'delivery_toast';
      text: string;
      flavor: 'ping' | 'success' | 'fail' | 'info';
    };

export const DELIVERY_TRIGGER_RADIUS_M = 10;
